// ---------- le décor d'un thème : un SVG déposé dans l'atelier, servi tel quel au jeu ----------
//
// Le décor arrive par l'API, pas avec l'application : c'est ce qui permet d'ouvrir un chapitre
// « En mer » sans publier de mise à jour Android. Mais un SVG peut porter du script, des liens,
// des images distantes ; injecté dans la page du jeu, ce serait une porte ouverte. D'où une LISTE
// BLANCHE stricte, et un refus plutôt qu'un nettoyage silencieux : l'atelier montre ce qui ne
// passe pas, au lieu d'enregistrer un décor amputé dont on ne comprendrait pas le rendu.
//
// Le mouvement n'est PAS dans le fichier : un élément choisit une animation en portant une des
// classes de ANIMATIONS, que le jeu définit une fois pour toutes (src/css). Un nouveau décor
// n'a donc besoin d'aucune mise à jour tant qu'il puise dans cette liste ; un nouveau mouvement,
// si.
//
// Écrit sans DOMParser : le Worker n'en a pas. Un lecteur de balises suffit, parce que la liste
// blanche n'admet ni texte, ni commentaire, ni CDATA, ni entité.

/** Les mouvements que le jeu sait jouer. Le nom est le contrat avec src/css : ne pas en retirer. */
export const ANIMATIONS = [
  'vg-sway',
  'vg-sway-top',
  'vg-fly',
  'vg-fly-back',
  'vg-cloud',
  'vg-cloud-slow',
  'vg-wave',
  'vg-wave-back',
  'vg-rock',
  'vg-jump',
  'vg-steam',
  'vg-twinkle',
  'vg-flap',
] as const;

const ELEMENTS = new Set([
  'svg',
  'g',
  'path',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
]);
// ---------- ce que chaque attribut peut valoir ----------
// Une liste d'attributs ne suffisait pas : la valeur, elle, était libre, à une liste NOIRE près
// (`url(`, `javascript:`…). Une liste noire se contourne — `u\rl(` et `\75 rl(` sont des `url(`
// pour le moteur CSS du navigateur (les attributs de présentation d'un SVG, `fill` et `stroke` en
// tête, sont lus comme des propriétés CSS, échappements compris), et passaient le motif. Une
// `fill="url(https://…)"` fait charger une ressource distante au jeu : de quoi savoir qui joue,
// et quand.
//
// D'où une GRAMMAIRE par attribut : ce qu'un décor dessiné à la main (et les six de départ, relus
// par backend/scripts/seed-content.ts) emploie réellement, et rien d'autre. Des nombres pour la
// géométrie, les lettres de commande d'un tracé pour `d`, des fonctions de transformation connues,
// et pour les couleurs un `#hex`, un `rgb()`/`hsl()` numérique, un nom de couleur ou `none`. Aucune
// de ces formes ne laisse passer une parenthèse ouverte sur autre chose qu'une fonction nommée, ni
// une barre oblique inverse : il n'y a plus rien à échapper.

const NUM = String.raw`[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?`;
const SEP = String.raw`(?:\s*,\s*|\s+)`;
const whole = (re: string): RegExp => new RegExp(`^\\s*(?:${re})\\s*$`);
const numbers = (): RegExp => whole(`${NUM}(?:${SEP}${NUM})*`);

/** Une longueur : un nombre, en unités utilisateur, en pixels ou en pourcentage. */
const LENGTH = whole(`${NUM}(?:px|%)?`);
/** Une opacité : de 0 à 1, ou un pourcentage. */
const OPACITY = whole(`${NUM}%?`);
const COLOR_FN = String.raw`(?:rgba?|hsla?)\(\s*${NUM}%?(?:(?:\s*,\s*|\s+|\s*\/\s*)${NUM}%?){2,3}\s*\)`;
/** Une couleur : `#rgb(a)`, `#rrggbb(aa)`, une fonction numérique, un mot (`white`, `none`,
 *  `transparent`, `currentColor`). Un mot seul ne peut rien appeler : il n'a pas de parenthèse. */
const PAINT = whole(`#[0-9a-fA-F]{3,4}|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{8}|${COLOR_FN}|[a-zA-Z]{3,20}`);
const TRANSFORM_FN = String.raw`(?:translate|rotate|scale|matrix|skewX|skewY)\(\s*${NUM}(?:${SEP}${NUM})*\s*\)`;

const GRAMMAR: Record<string, RegExp> = {
  viewbox: whole(`${NUM}${SEP}${NUM}${SEP}${NUM}${SEP}${NUM}`),
  width: LENGTH,
  height: LENGTH,
  // Les commandes d'un tracé et des nombres. Aucune autre lettre : `url` n'y entre pas.
  d: /^[MmLlHhVvCcSsQqTtAaZz0-9.,\s+eE-]*$/,
  cx: LENGTH,
  cy: LENGTH,
  r: LENGTH,
  rx: LENGTH,
  ry: LENGTH,
  x: LENGTH,
  y: LENGTH,
  x1: LENGTH,
  y1: LENGTH,
  x2: LENGTH,
  y2: LENGTH,
  points: numbers(),
  fill: PAINT,
  'fill-opacity': OPACITY,
  stroke: PAINT,
  'stroke-width': LENGTH,
  'stroke-linecap': /^(?:butt|round|square)$/,
  'stroke-linejoin': /^(?:miter|round|bevel|arcs|miter-clip)$/,
  'stroke-dasharray': whole(`none|${NUM}(?:px|%)?(?:${SEP}${NUM}(?:px|%)?)*`),
  'stroke-opacity': OPACITY,
  opacity: OPACITY,
  transform: whole(`${TRANSFORM_FN}(?:(?:\\s*,\\s*|\\s+)?${TRANSFORM_FN})*`),
  // Contrôlée à part, contre ANIMATIONS.
  class: /^[a-z0-9\s-]*$/,
  xmlns: /^http:\/\/www\.w3\.org\/2000\/svg$/,
};
/** Ce qu'une valeur d'attribut ne contient jamais, quelle que soit sa grammaire : une adresse, un
 *  appel, une échappée, une barre oblique inverse. Redondant avec GRAMMAR, et voulu : si une
 *  grammaire s'élargit un jour par mégarde, ce filet reste tendu. */
const FORBIDDEN_VALUE = /url\s*\(|javascript:|data:|expression\s*\(|[<>&\\]/i;
/** La taille d'un décor : quelques dizaines de formes. Au-delà, c'est une illustration
 *  exportée, trop lourde à recopier dans chaque réponse de l'API. */
export const MAX_DECOR_BYTES = 40_000;

export type DecorResult = { ok: true; svg: string } | { ok: false; error: string };

/** Contrôle un décor et le rend sous une forme normalisée (balises en minuscules, espaces réduits). */
export function checkDecor(input: string): DecorResult {
  const src = input.trim();
  if (!src) return { ok: false, error: 'décor vide' };
  if (src.length > MAX_DECOR_BYTES)
    return {
      ok: false,
      error: `décor trop lourd (${src.length} caractères, ${MAX_DECOR_BYTES} au plus)`,
    };
  const tag = /<(\/?)([a-zA-Z]+)((?:\s+[a-zA-Z:-]+\s*=\s*"[^"]*")*)\s*(\/?)>/g;
  const out: string[] = [];
  const stack: string[] = [];
  let at = 0;
  let closed = false;
  for (let m = tag.exec(src); m; m = tag.exec(src)) {
    if (src.slice(at, m.index).trim())
      return {
        ok: false,
        error: `texte ou balise non reconnue vers « ${src.slice(at, at + 30).trim()} »`,
      };
    at = m.index + m[0].length;
    const [, closing, rawName, rawAttrs, selfClosing] = m;
    const name = rawName.toLowerCase();
    if (closed) return { ok: false, error: 'du contenu après la fin du <svg>' };
    if (!ELEMENTS.has(name)) return { ok: false, error: `élément interdit : <${rawName}>` };
    if (closing) {
      if (stack.pop() !== name) return { ok: false, error: `</${name}> ne ferme rien` };
      out.push(`</${name}>`);
      closed = !stack.length;
      continue;
    }
    const attrs: string[] = [];
    for (const a of rawAttrs.matchAll(/([a-zA-Z:-]+)\s*=\s*"([^"]*)"/g)) {
      const key = a[1].toLowerCase();
      const value = a[2];
      // Une propriété PROPRE et non `in` : `constructor="…"` trouverait sinon une « grammaire » dans
      // le prototype. Et pas `Object.hasOwn` : ce code tourne aussi dans la WebView Android du jeu
      // (src/ui.ts), où il n'existe qu'à partir de Chrome 93.
      if (!Object.prototype.hasOwnProperty.call(GRAMMAR, key))
        return { ok: false, error: `attribut interdit : ${a[1]} (sur <${name}>)` };
      if (FORBIDDEN_VALUE.test(value) || !GRAMMAR[key].test(value))
        return { ok: false, error: `valeur interdite pour ${a[1]} : « ${value.slice(0, 30)} »` };
      if (key === 'class') {
        const bad = value
          .split(/\s+/)
          .filter((c) => c && !(ANIMATIONS as readonly string[]).includes(c));
        if (bad.length)
          return {
            ok: false,
            error: `classe inconnue : ${bad.join(', ')} (permises : ${ANIMATIONS.join(', ')})`,
          };
      }
      attrs.push(`${key === 'viewbox' ? 'viewBox' : key}="${value}"`);
    }
    if (!stack.length && name !== 'svg')
      return { ok: false, error: 'le décor doit commencer par <svg>' };
    if (stack.length && name === 'svg') return { ok: false, error: '<svg> imbriqué' };
    out.push(`<${name}${attrs.length ? ' ' + attrs.join(' ') : ''}${selfClosing ? ' /' : ''}>`);
    if (selfClosing) closed = !stack.length;
    else stack.push(name);
  }
  if (src.slice(at).trim())
    return {
      ok: false,
      error: `texte ou balise non reconnue vers « ${src.slice(at, at + 30).trim()} »`,
    };
  if (stack.length) return { ok: false, error: `<${stack[stack.length - 1]}> n'est pas fermé` };
  if (!out.length) return { ok: false, error: 'aucun <svg>' };
  return { ok: true, svg: out.join('') };
}
